import React, {useState} from 'react';
import {View, ScrollView} from 'react-native';
import {Divider} from 'react-native-paper';
import _ from 'lodash';

import sy from '~/styles';
import {
  Text,
  ActionButton,
  WarrantyIcon,
  ReplacementIcon,
  ReplacementText,
  ApproveToggle,
  Dialog,
  Pressable,
  Checkbox,
} from '~/components/controls';
import Actionsheet, {
  Title,
  Content,
  Actions,
} from '~/components/controls/Actionsheet';
import Format from '~/lib/format';
import withDimensions from '~/components/hoc/with-dimensions';
import {toggleDeniedDialog} from '~/actions';
import {useDispatch, useSelector} from '~/lib/hooks';

import BuildIcon from '~/images/md-icons/build_repair/materialicons/24px.svg';
import WarningIcon from '~/images/md-icons/warning/materialsymbolsoutlined/24px.svg';

const Part = ({parts}) => {
  return (
    <>
      {parts?.map((item, index) => {
        const description = item.description;
        const amount = item.amount;
        const number = item.number;

        return (
          <React.Fragment key={item.id}>
            <View style={[sy['flex-row']]}>
              <View
                style={[
                  sy['flex-row'],
                  sy['flex-1'],
                  sy['justify-between'],
                  sy['p-4'],
                ]}>
                <View style={[sy['flex-1']]}>
                  <Text>{description}</Text>
                  {!_.isNil(number) && (
                    <Text style={[sy.small, sy['text-lightgray']]}>
                      {number}
                    </Text>
                  )}
                </View>
                <View style={[sy['self-start'], sy['items-end']]}>
                  <Text style={[sy.smallRegular]}>{Format.price(amount)}</Text>
                </View>
              </View>
            </View>
            {index < parts.length - 1 && (
              <Divider style={[{backgroundColor: '#f2f2f2'}]} />
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const DeniedInfoDialog = ({visible, onDismiss}) => {
  const dispatch = useDispatch();
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  return (
    <Dialog
      title="Activiteit afkeuren"
      titleIcon={<WarningIcon fill="#FF6C00" />}
      onDismiss={onDismiss}
      visible={visible}
      options={{
        noPaddingContent: true,
      }}
      buttons={[
        {
          text: 'OK',
          onPress: async () => {
            if (neverShowAgain) {
              dispatch(toggleDeniedDialog());
            }

            onDismiss();
          },
        },
      ]}>
      <View style={[sy['py-4'], sy['px-6']]}>
        <Text>
          Wanneer je een activiteit afkeurt kan dit gevolgen hebben voor de
          uitvoering en/of prijs van andere activiteiten in de opdracht.
        </Text>
      </View>
      <Divider />
      <Pressable
        style={[
          sy['py-4'],
          sy['px-6'],
          sy['gap-4'],
          sy['flex-row'],
          sy['items-center'],
        ]}
        onPress={() => setNeverShowAgain(!neverShowAgain)}>
        <Checkbox checked={neverShowAgain} />
        <Text>Niet meer tonen</Text>
      </Pressable>
    </Dialog>
  );
};

const HEADER_HEIGHT_IN_PX = 72;
const FOOTER_HEIGHT_IN_PX = 52;

const Task = ({
  dimensions,
  onDismiss,
  is_denied,
  component,
  is_replacement,
  onChange,
}) => {
  const hide_denied_dialog = useSelector((state) => state.hide_denied_dialog);

  const [taskDenied, setTaskDenied] = useState(is_denied);
  const [showDeniedInfoDialog, setShowDeniedInfoDialog] = useState(false);

  const onOK = async () => {
    await onChange(component.id, taskDenied);
    onDismiss();
  };

  return (
    <>
      <Actionsheet visible={true} onDismiss={onDismiss}>
        <Title>
          <View
            style={[
              sy['flex-row'],
              sy['justify-between'],
              sy['items-center'],
              sy['flex-1'],
              {
                width: '100%',
              },
            ]}>
            <Text style={[sy.largePlus, sy.truncate, {lineHeight: 30}]}>
              {component?.name}
            </Text>
            <ApproveToggle
              denied={taskDenied}
              onPress={(denied) => {
                if (denied && !hide_denied_dialog) {
                  setShowDeniedInfoDialog(true);
                }

                setTaskDenied(denied);
              }}
            />
          </View>
        </Title>
        <Divider />

        <Content style={{paddingBottom: 0, paddingHorizontal: 0}}>
          <ScrollView
            contentContainerStyle={{
              maxHeight:
                dimensions.window.height -
                HEADER_HEIGHT_IN_PX -
                FOOTER_HEIGHT_IN_PX,
            }}>
            <View
              style={[
                sy['p-4'],
                sy['flex-row'],
                sy['justify-between'],
                sy['flex-1'],
                sy['gap-4'],
              ]}>
              <View style={[sy['flex-row'], sy['flex-1'], sy['gap-8']]}>
                {is_replacement ? (
                  <ReplacementIcon type="transport" />
                ) : (
                  <BuildIcon fill="#4A4A49" width={24} height={24} />
                )}
                <Text style={[sy.truncate]}>
                  {is_replacement ? (
                    <ReplacementText type="transport" />
                  ) : (
                    <>{component.work_text ?? component.description}</>
                  )}
                </Text>
              </View>
              <View>
                <View style={[sy['self-start'], sy['items-end']]}>
                  <View>
                    <Text style={[sy.smallRegular]}>
                      {Format.price(component.amount)}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
            {component.parts?.length > 0 && (
              <>
                <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                <View
                  style={[sy['p-4'], sy['flex-row'], sy['justify-between']]}>
                  <Text style={sy.mediumPlus}>Artikel(en)</Text>
                </View>
                <Divider style={[{backgroundColor: '#f2f2f2'}]} />
                <View>
                  <Part parts={component.parts} />
                </View>
                <Divider style={[{backgroundColor: '#f2f2f2'}, sy['ml-18']]} />
              </>
            )}
          </ScrollView>
        </Content>
        <Divider />
        <Actions style={[sy['justify-between']]}>
          <View style={[sy['flex-row'], sy['items-center']]}>
            <Text style={[sy.mediumBold, sy['pl-2'], sy['pr-4']]}>
              {Format.price(component.task_amount)}
            </Text>
            <View style={[sy['flex-row'], sy['items-center']]}>
              {(component.warranty || component.is_damage) && (
                <WarrantyIcon
                  value={{
                    warranty: component.warranty,
                    is_damage: component.is_damage,
                  }}
                />
              )}
            </View>
          </View>
          <View style={[sy['flex-row'], sy['gap-2']]}>
            <ActionButton onPress={onDismiss}>Annuleren</ActionButton>
            <ActionButton onPress={onOK}>OK</ActionButton>
          </View>
        </Actions>
      </Actionsheet>
      <DeniedInfoDialog
        visible={showDeniedInfoDialog}
        onDismiss={() => setShowDeniedInfoDialog(false)}
      />
    </>
  );
};

export default withDimensions(Task);
